import { CircularProgress, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { useMutation } from "@tanstack/react-query";
import { useUserContext } from "context/userContext";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { BtnStyle1 } from "styles/buttonStyle";
import { snackBarStyle } from "styles/snackBarStyle";
import { textFieldStyles } from "styles/textFieldStyles";
import * as Yup from "yup";
import { login } from "../api/Login";
import { useCart } from "../context/CartContext";

const linkStyles = {
  color: "#424242",
  textDecoration: "none",
  position: "relative",
  "&:after": {
    content: '""',
    position: "absolute",
    width: "100%",
    height: "1px",
    bottom: "0",
    left: "0",
    backgroundColor: "#424242",
    transform: "scaleX(0)",
    transformOrigin: "bottom right",
    transition: "transform 0.25s ease-out",
  },
  "&:hover:after": {
    transform: "scaleX(1)",
    transformOrigin: "bottom left",
  },
};

const CustomLink = (props) => (
  <Box component={Link} sx={linkStyles} {...props} />
);

export default function LoginPage({
  onSignIn = () => {},
  showForgetPassForm,
  closeLoginForm,
}) {
  const { enqueueSnackbar } = useSnackbar();

  const { setIsAuth_, setUser } = useUserContext();

  const { getCarts } = useCart();

  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      if (data.status === "success") {
        getCarts();
        onSignIn();
        setIsAuth_(true);
        navigate("/");
        enqueueSnackbar("Log ind med succes", snackBarStyle());
      } else {
        enqueueSnackbar("Der skete en fejl. Prøv igen", snackBarStyle("error"));
      }
    },
    onError: (error) => {
      enqueueSnackbar("Der skete en fejl. Prøv igen", snackBarStyle("error"));
    },
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().email("Ugyldig e-mailadresse").required("Required"),
      password: Yup.string().required("Required"),
    }),
    onSubmit: (values) => {
      mutation.mutate(values);
    },
  });

  return (
    <Box component="main">
      <Box
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <Typography
          variant="h6"
          component="h2"
          sx={{ textAlign: "left", mb: "5px" }}
        >
          Log ind på din konto
        </Typography>
        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          noValidate
          sx={{ mt: 1, width: 1 }}
        >
          <Box mb={2} position={"relative"}>
            <TextField
              margin="none"
              required
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              type="text"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              sx={{ ...textFieldStyles, width: 1 }}
            />
            {formik.touched.email && Boolean(formik.errors.email) && (
              <Typography
                component={"p"}
                mt={0.5}
                position={"absolut"}
                fontSize={12}
                color={"error"}
              >
                {formik.errors.email}
              </Typography>
            )}
          </Box>
          <Box mb={2} position={"relative"}>
            <TextField
              margin="none"
              required
              id="password"
              label="Adgangskode"
              name="password"
              autoComplete="password"
              type="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              sx={{ ...textFieldStyles, width: 1 }}
            />
            {formik.touched.password && Boolean(formik.errors.password) && (
              <Typography
                component={"p"}
                mt={0.5}
                position={"absolut"}
                fontSize={12}
                color={"error"}
              >
                {formik.errors.password}
              </Typography>
            )}
          </Box>
          <Grid container justifyContent="flex-end">
            <Grid item>
              <Button sx={BtnStyle1} onClick={showForgetPassForm}>
                Glemt Kodeord?
              </Button>
            </Grid>
          </Grid>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              mb: 2,
              height: "50px",
              bgcolor: "#424242",
              "&:hover": {
                bgcolor: "#686868",
              },
            }}
            disabled={mutation.isLoading}
          >
            {mutation.isPending ? (
              <CircularProgress sx={{ color: "white" }} size={"25px"} />
            ) : (
              "Log ind"
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
